import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Form, message, Modal } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import getApolloErrors from '../../../../server/utils/getApolloErrors';
import * as permissionKind from '../../../../shared/permissions';
import { type LocalModelWithPermissionsSpecsFragment } from '../../../api/fragments/LocalModelWithPermissionsSpecs';
import {
    DeleteVehicleDocument,
    type DeleteVehicleMutation,
    type DeleteVehicleMutationVariables,
} from '../../../api/mutations/deleteVehicle';
import {
    UpdateModelDocument,
    type UpdateModelMutation,
    type UpdateModelMutationVariables,
} from '../../../api/mutations/updateModel';
import { type LocalModelSettings } from '../../../api/types';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../utilities/useFormattedSimpleVersioning';
import useHandleError from '../../../utilities/useHandleError';
import AddSubmodelDetails from '../AddVehiclePage/AddSubmodelPage/AddSubmodelDetails';
import useSubmodelValidator from '../AddVehiclePage/AddSubmodelPage/useSubmodelValidator';

export type SubmodelDetailsPageProps = { submodel: LocalModelWithPermissionsSpecsFragment };

type FormValues = LocalModelSettings;

const SubmodelDetailsPage = ({ submodel }: SubmodelDetailsPageProps) => {
    const { t } = useTranslation(['submodel']);
    const navigate = useNavigate();
    const apolloClient = useApolloClient();
    const validator = useSubmodelValidator();
    const submodelId = submodel.id;
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: submodel.versioning,
        moduleId: submodel.moduleId,
    });

    const onSubmit = useHandleError<FormValues>(
        async values => {
            // submitting message
            message.loading({
                content: t('submodel:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // submit update
            await apolloClient
                .mutate<UpdateModelMutation, UpdateModelMutationVariables>({
                    mutation: UpdateModelDocument,
                    variables: { id: submodelId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // inform about success
            message.success({
                content: t('submodel:messages.updateSuccessful'),
                key: 'primary',
            });

            navigate('/admin/vehicles/submodels');
        },
        [apolloClient, t, submodelId, navigate]
    );

    const deleteSubmodel = useCallback(() => {
        Modal.confirm({
            title: t('submodel:deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('submodel:deleteModal.content'),
            okText: t('submodel:deleteModal.okText'),
            okType: 'danger',
            cancelText: t('submodel:deleteModal.cancelText'),
            async onOk() {
                try {
                    message.loading({
                        content: t('submodel:message.deleteSubmitting'),
                        key: 'primary',
                        duration: 0,
                    });

                    // delete with API
                    await apolloClient
                        .mutate<DeleteVehicleMutation, DeleteVehicleMutationVariables>({
                            mutation: DeleteVehicleDocument,
                            variables: { id: submodelId },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    message.success({
                        content: t('submodel:messages.deleteSuccessful'),
                        key: 'primary',
                    });

                    navigate('/admin/vehicles/submodels');
                } catch (error) {
                    const apolloErrors = getApolloErrors(error);

                    if (apolloErrors?.$root) {
                        message.error(apolloErrors?.$root);
                    }
                }
            },
        });
    }, [apolloClient, t, submodelId, navigate]);

    const initialValues = useMemo(
        (): FormValues => ({
            ...pick(['name', 'identifier', 'makeId', 'isActive', 'order', 'parentModelId', 'bodyType'], submodel),
        }),
        [submodel]
    );

    const [hasUpdatePermission, hasDeletePermission] = useMemo(
        () => [
            hasPermissions(submodel.permissions, [permissionKind.updateVehicle]),
            hasPermissions(submodel.permissions, [permissionKind.deleteVehicle]),
        ],
        [submodel]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validator}>
            {({ handleSubmit }) => (
                <ConsolePageWithHeader
                    extra={
                        hasDeletePermission && (
                            <Button onClick={deleteSubmodel} danger>
                                {t('submodel:actions.delete')}
                            </Button>
                        )
                    }
                    footer={[
                        hasUpdatePermission && (
                            <Button key="submodelUpdate" form="submodelUpdate" htmlType="submit" type="primary">
                                {t('submodel:actions.update')}
                            </Button>
                        ),
                    ]}
                    onBack={() => navigate('/admin/vehicles/submodels')}
                    title={t('submodel:title', { name: initialValues.name.defaultValue, id: initialValues.identifier })}
                >
                    <Form id="submodelUpdate" layout="vertical" name="submodelUpdate" onSubmitCapture={handleSubmit}>
                        <AddSubmodelDetails
                            disabled={!hasUpdatePermission}
                            initialValues={initialValues}
                            offset={offset}
                            updated={updated}
                        />
                    </Form>
                </ConsolePageWithHeader>
            )}
        </Formik>
    );
};

export default SubmodelDetailsPage;
